import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { api } from '@watchparty/shared';
// ---------------------------------------------------------------------------
// SectionHeading
// ---------------------------------------------------------------------------
function SectionHeading({ icon: Icon, label }) {
    return (_jsxs("div", { style: {
            display: 'flex', alignItems: 'center', gap: '10px',
            marginBottom: '20px',
        }, children: [_jsx(Icon, { size: 18 }), _jsx("h2", { style: {
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    margin: 0,
                }, children: label })] }));
}
// ---------------------------------------------------------------------------
// ToggleRow
// ---------------------------------------------------------------------------
function ToggleRow({ label, description, checked, onChange, disabled, }) {
    return (_jsxs("label", { style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '16px',
            padding: '14px 0',
            borderBottom: '1px solid var(--color-border)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
        }, children: [_jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("div", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: 'var(--color-text)',
                        }, children: label }), _jsx("div", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.8125rem',
                            color: 'var(--color-text-muted)',
                            marginTop: '2px',
                        }, children: description })] }), _jsx("div", { role: "switch", "aria-checked": checked, onClick: (e) => {
                    e.preventDefault();
                    if (!disabled)
                        onChange(!checked);
                }, style: {
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    background: checked ? 'var(--color-accent)' : 'var(--color-border-strong)',
                    position: 'relative',
                    flexShrink: 0,
                    transition: 'background 200ms',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                }, children: _jsx("div", { style: {
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: '3px',
                        left: checked ? '23px' : '3px',
                        transition: 'left 150ms ease',
                    } }) })] }));
}
// ---------------------------------------------------------------------------
// NotificationSettings
// ---------------------------------------------------------------------------
function NotificationSettings() {
    const auth = useAuth();
    const [notifyDiscussion, setNotifyDiscussion] = useState(auth.user?.notifyDiscussion ?? true);
    const [notifyWatchActivity, setNotifyWatchActivity] = useState(auth.user?.notifyWatchActivity ?? true);
    const [isSaving, setIsSaving] = useState(false);
    const debounceRef = useRef(null);
    const save = useCallback((discussion, watchActivity) => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                await api.patch('/auth/me/notifications', {
                    notifyDiscussion: discussion,
                    notifyWatchActivity: watchActivity,
                });
                await auth.refreshUser();
            }
            catch {
                // silently fail — toggle will be out of sync until next refresh
            }
            finally {
                setIsSaving(false);
            }
        }, 500);
    }, [auth]);
    function handleDiscussionChange(value) {
        setNotifyDiscussion(value);
        save(value, notifyWatchActivity);
    }
    function handleWatchActivityChange(value) {
        setNotifyWatchActivity(value);
        save(notifyDiscussion, value);
    }
    return (_jsxs("section", { style: { marginBottom: '48px' }, children: [_jsx(SectionHeading, { icon: Bell, label: "Notifications" }), _jsxs("div", { style: { position: 'relative' }, children: [_jsx(ToggleRow, { label: "Discussion replies", description: "Get notified when someone replies in a discussion you're part of", checked: notifyDiscussion, onChange: handleDiscussionChange, disabled: isSaving }), _jsx(ToggleRow, { label: "Watch activity", description: "Get notified when a party member watches or rates something", checked: notifyWatchActivity, onChange: handleWatchActivityChange, disabled: isSaving }), isSaving && (_jsxs("div", { style: {
                            position: 'absolute', top: '8px', right: '0',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-muted)',
                        }, children: [_jsx(Loader2, { size: 12, style: { animation: 'spin 1s linear infinite' } }), "Saving..."] }))] })] }));
}
// ---------------------------------------------------------------------------
// DangerZone
// ---------------------------------------------------------------------------
function DangerZone() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    async function handleDelete() {
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await api.delete('/auth/me');
            await auth.logout();
            navigate({ to: '/' });
        }
        catch {
            setDeleteError('Failed to delete account. Please try again.');
            setIsDeleting(false);
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("section", { children: [_jsx(SectionHeading, { icon: AlertTriangle, label: "Danger Zone" }), _jsx("div", { style: {
                            border: '1px solid var(--color-danger, #ef4444)',
                            borderRadius: 'var(--radius-md)',
                            padding: '20px',
                        }, children: _jsxs("div", { style: {
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: '16px', flexWrap: 'wrap',
                            }, children: [_jsxs("div", { children: [_jsx("div", { style: {
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                color: 'var(--color-text)',
                                            }, children: "Delete account" }), _jsx("div", { style: {
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.8125rem',
                                                color: 'var(--color-text-muted)',
                                                marginTop: '4px',
                                            }, children: "Permanently delete your account and all associated data. This cannot be undone." })] }), _jsx("button", { onClick: () => setShowModal(true), style: {
                                        padding: '8px 20px',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-danger, #ef4444)',
                                        background: 'transparent',
                                        color: 'var(--color-danger, #ef4444)',
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        transition: 'background 80ms ease',
                                    }, onMouseEnter: e => {
                                        e.currentTarget.style.background = 'var(--color-danger, #ef4444)';
                                        e.currentTarget.style.color = '#fff';
                                    }, onMouseLeave: e => {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = 'var(--color-danger, #ef4444)';
                                    }, children: "Delete account" })] }) })] }), showModal && (_jsx("div", { onClick: () => { if (!isDeleting)
                    setShowModal(false); }, style: {
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 100, padding: '16px',
                }, children: _jsxs("div", { onClick: e => e.stopPropagation(), style: {
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg, 12px)',
                        padding: '28px',
                        width: '100%',
                        maxWidth: '420px',
                    }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }, children: [_jsx(AlertTriangle, { size: 20, style: { color: 'var(--color-danger, #ef4444)', flexShrink: 0 } }), _jsx("h3", { style: {
                                        fontFamily: 'var(--font-family-heading)',
                                        fontSize: '1.125rem',
                                        fontWeight: 700,
                                        color: 'var(--color-text)',
                                        margin: 0,
                                    }, children: "Delete your account?" })] }), _jsx("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-muted)',
                                lineHeight: 1.5,
                                marginBottom: '20px',
                            }, children: "This will permanently delete your account, your parties, ratings, messages, and all associated data. This action cannot be undone." }), _jsxs("label", { style: {
                                display: 'block',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: 'var(--color-text)',
                                marginBottom: '8px',
                            }, children: ["Type ", _jsx("strong", { children: "DELETE" }), " to confirm"] }), _jsx("input", { value: confirmText, onChange: e => setConfirmText(e.target.value), placeholder: "DELETE", disabled: isDeleting, style: {
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-surface)',
                                color: 'var(--color-text)',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.9375rem',
                                outline: 'none',
                                boxSizing: 'border-box',
                                marginBottom: '16px',
                            } }), deleteError && (_jsx("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.8125rem',
                                color: 'var(--color-danger, #ef4444)',
                                marginBottom: '12px',
                            }, children: deleteError })), _jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'flex-end' }, children: [_jsx("button", { onClick: () => setShowModal(false), disabled: isDeleting, style: {
                                        padding: '8px 20px',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)',
                                        background: 'transparent',
                                        color: 'var(--color-text)',
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    }, children: "Cancel" }), _jsx("button", { onClick: handleDelete, disabled: confirmText !== 'DELETE' || isDeleting, style: {
                                        padding: '8px 20px',
                                        borderRadius: 'var(--radius-md)',
                                        border: 'none',
                                        background: confirmText === 'DELETE' && !isDeleting
                                            ? 'var(--color-danger, #ef4444)'
                                            : 'var(--color-border)',
                                        color: confirmText === 'DELETE' && !isDeleting ? '#fff' : 'var(--color-text-muted)',
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        cursor: confirmText !== 'DELETE' || isDeleting ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                    }, children: isDeleting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 14, style: { animation: 'spin 1s linear infinite' } }), " Deleting..."] })) : (_jsxs(_Fragment, { children: [_jsx(Trash2, { size: 14 }), " Delete account"] })) })] })] }) }))] }));
}
// ---------------------------------------------------------------------------
// AccountPage
// ---------------------------------------------------------------------------
export function AccountPage() {
    return (_jsxs("div", { style: { maxWidth: '640px', margin: '0 auto', padding: '24px 16px' }, children: [_jsx("h1", { style: {
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    marginBottom: '32px',
                }, children: "Account" }), _jsx(NotificationSettings, {}), _jsx(DangerZone, {})] }));
}
